import React from 'react';
import cx from 'classnames';
import { List as TabsList, Trigger as TabsTrigger } from '@radix-ui/react-tabs';
import { TabListTreeNodeDataProps } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { TabGroupContext } from './TabGroup';

type TabListProps = {
  id: string;
  editorClassName?: string;
  children: React.ReactNode;
  isEditor?: boolean;
} & TabListTreeNodeDataProps;

export const TabList = React.forwardRef<HTMLDivElement, TabListProps>(
  (
    {
      id,
      className,
      editorClassName,
      children,
      isEditor = false,
      // `type` is a tree-node schema discriminator, not a DOM attribute.
      type: _type,
      ...props
    },
    ref,
  ) => {
    const ctx = React.useContext(TabGroupContext);
    const innerRef = React.useRef<HTMLDivElement | null>(null);
    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        innerRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          // eslint-disable-next-line no-param-reassign
          ref.current = node;
        }
      },
      [ref],
    );

    const childArray = React.Children.toArray(children);
    const activeIndex = ctx?.activeIndex ?? 0;
    const tabIds = ctx?.tabIds ?? [];

    // Editor mode: user-authored tab buttons are wrapped in DndComponents that swallow Slot props,
    // so Tabs.Trigger asChild can't reach them. Mirror aria-selected/data-state onto each direct
    // child's role="tab" descendant after every render — childCount alone misses drag-reorders
    // that preserve the count, which would otherwise leave the active marker on the wrong button.
    React.useEffect(() => {
      if (!isEditor) return;
      const list = innerRef.current;
      if (!list) return;
      Array.from(list.children).forEach((wrapper, i) => {
        const tabEl = wrapper.matches('[role="tab"]') ? wrapper : wrapper.querySelector('[role="tab"]');
        if (!tabEl) return;
        const isActive = i === activeIndex;
        tabEl.setAttribute('aria-selected', String(isActive));
        tabEl.setAttribute('data-state', isActive ? 'active' : 'inactive');
      });
    });

    // Editor mode: capture clicks before DndComponent's stopPropagation in the bubble phase.
    const handleClickCapture = React.useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isEditor || !ctx) return;
        const { target } = e;
        if (!(target instanceof Node)) return;
        const list = e.currentTarget;
        const idx = Array.from(list.children).findIndex((c) => c === target || c.contains(target));
        if (idx >= 0) ctx.setActiveIndex(idx);
      },
      [isEditor, ctx],
    );

    return (
      <TabsList asChild>
        <div
          ref={setRefs}
          id={id}
          className={cx(className, editorClassName)}
          onClickCapture={isEditor ? handleClickCapture : undefined}
          {...props}
        >
          {childArray.map((child, index) => {
            const key = tabIds[index] ?? index;
            return isEditor ? (
              <React.Fragment key={key}>{child}</React.Fragment>
            ) : (
              <TabsTrigger key={key} value={`tab-${index}`} asChild>
                {child}
              </TabsTrigger>
            );
          })}
        </div>
      </TabsList>
    );
  },
);

TabList.displayName = 'TabList';
